import { XHRRequestCanceler } from '../../../../Libs/xhr/XHRRequestCanceler';
import {
    IMaterielDataService,
    MaterielDataServiceName,
    MaterielEndpoints,
} from '../../../DataServices/MaterielDataService';
import { Lazy } from '../../../Helpers/Lazy';
import { ApiDataResponse, BaseApiDataResponse } from '../../../Models/ApiData';
import {
    Materiel,
    MaterielDetailsApiDataResponse,
    MaterielListOrder,
    MaterielListResponse,
} from '../../../Models/Materiels/Materiel';
import { ExportFileType, ExportUtils } from '../../../Utils/ExportUtils';
import { IoC } from '../../ServicesContainer';
import { BaseReduxService } from '../Api/BaseReduxService';
import { AuthServiceName, IAuthService } from '../Authentication/AuthenticationService';
import { DownloadServiceName, IDownloadService } from '../Download/DownloadService';
import { I18nServiceName, II18nService } from '../I18n/I18nService';

export interface IMaterielService {
    listMateriel: (
        cts: XHRRequestCanceler,
        params: {
            search?: string;
            order?: MaterielListOrder;
            is_actif?: boolean;
        }
    ) => Promise<MaterielListResponse>;

    getMateriel: (
        cts: XHRRequestCanceler,
        materielId: string
    ) => Promise<MaterielDetailsApiDataResponse>;

    updateMateriel: (
        materielId: string,
        materiel: Materiel,
        cts: XHRRequestCanceler
    ) => Promise<BaseApiDataResponse>;

    createMateriel: (
        materiel: Materiel,
        cts: XHRRequestCanceler
    ) => Promise<ApiDataResponse<string>>;

    exportMaterielList: (
        cts: XHRRequestCanceler,
        search: string,
        order?: MaterielListOrder,
        is_actif?: boolean
    ) => void;
}

class MaterielService extends BaseReduxService implements IMaterielService {
    private readonly materielDataService: Lazy<IMaterielDataService>;
    private readonly i18nService: Lazy<II18nService>;
    private readonly downloadService: Lazy<IDownloadService>;
    private readonly authService: Lazy<IAuthService>;

    constructor() {
        super();
        this.materielDataService = IoC.getLazy<IMaterielDataService>(MaterielDataServiceName);
        this.i18nService = IoC.getLazy<II18nService>(I18nServiceName);
        this.downloadService = IoC.getLazy<IDownloadService>(DownloadServiceName);
        this.authService = IoC.getLazy<IAuthService>(AuthServiceName);
    }

    public listMateriel = async (
        cts: XHRRequestCanceler,
        params: {
            search?: string;
            order?: MaterielListOrder;
            is_actif?: boolean;
        }
    ) => {
        try {
            return this.materielDataService.value().listMateriel(
                {
                    search: params.search,
                    order: params.order,
                    is_actif: params.is_actif,
                },
                cts
            );
        } catch (err) {
            return Promise.reject(err);
        }
    };

    public getMateriel = (cts: XHRRequestCanceler, materielId: string) => {
        try {
            return this.materielDataService.value().getMateriel(cts, materielId);
        } catch (err) {
            return Promise.reject(err);
        }
    };

    public updateMateriel = async (
        materielId: string,
        materiel: Materiel,
        cts: XHRRequestCanceler
    ) => {
        try {
            const response = await this.materielDataService
                .value()
                .updateMateriel(materielId, materiel, cts);
            if (!response.is_success) {
                this.toastApiError(
                    this.i18nService.value().getString(`Error_${response.error_code}` || '')
                );
            }
            return Promise.resolve(response);
        } catch (err) {
            return Promise.reject(err);
        }
    };

    public createMateriel = async (
        materiel: Materiel,
        cts: XHRRequestCanceler
    ): Promise<ApiDataResponse<string>> => {
        try {
            const response = await this.materielDataService.value().createMateriel(materiel, cts);
            if (!response.is_success) {
                this.toastApiError(
                    this.i18nService.value().getString(`Error_${response.error_code}` || '')
                );
            }
            return Promise.resolve(response);
        } catch (err) {
            this.toastApiError(this.i18nService.value().getString(`Error_${err.error_code}` || ''));
            return Promise.reject(err);
        }
    };

    public exportMaterielList = async (
        cts: XHRRequestCanceler,
        search: string,
        order?: MaterielListOrder | undefined,
        is_actif?: boolean
    ): Promise<void> => {
        try {
            const params: { [key: string]: string } = {};
            params['search'] = search;
            if (order) {
                params['order'] = order.valueOf();
            }
            if (is_actif === true) {
                params['is_actif'] = is_actif.valueOf().toString();
            }
            const url: string = ExportUtils.getExportUrlWithParams(
                {
                    url: MaterielEndpoints.EXPORT_MATERIEL_LIST,
                    auth: await this.authService.value().getAuthorization(),
                },
                params
            );
            this.downloadService.value().downloadFile(url, 'Materiels', ExportFileType.EXCEL, cts);
        } catch (err) {
            Promise.reject(err);
        }
    };
}

const MaterielServiceName = 'MaterielService';
export { MaterielService, MaterielServiceName };
